import { z } from "zod";

/**
 * Webapp-only billing contracts (per-feature usage counters,
 * subscription status, and Stripe checkout / billing-portal session
 * creation). The Stripe webhook route is not contracted here —
 * webhooks are Stripe-shaped. /api/billing/sync-subscription stays
 * uncontracted too: it's fire-and-forget and its response is never
 * read.
 */

// ── Shared enums ──────────────────────────────────────────────────

/**
 * Canonical wire enum for a plan. Mirrors BillingProduct in
 * webapp/lib/billing/constants.ts ("free" = no active subscription;
 * solo/pro/team are the DB org_subscriptions.product values, sold as
 * Starter/Growth/Enterprise). The usage handler assigns a
 * BillingProduct directly into this enum's slot, so adding a
 * BillingProduct member without widening this enum is a compile
 * error in app/api/billing/usage/route.ts. The webapp's PlanType
 * (stores/paymentSelection/types.ts) re-exports this inference.
 */
export const BillingPlan = z.enum(["free", "solo", "pro", "team"]);
export type BillingPlan = z.infer<typeof BillingPlan>;

/**
 * Plans purchasable through self-serve checkout — the key set of
 * PLAN_MAP in webapp/lib/billing/constants.ts ("free" has no Stripe
 * price). PLAN_MAP is typed against this enum, so the two can't
 * drift without a compile error.
 */
export const CheckoutPlanType = z.enum(["solo", "pro", "team"]);
export type CheckoutPlanType = z.infer<typeof CheckoutPlanType>;

/**
 * Canonical wire enum for a billing cycle — the inner key set of
 * PLAN_MAP. "annual" resolves to a monthly-interval Stripe price at
 * the discounted rate with an application-enforced 12-month commit
 * (see the checkout handler's subscription_data.metadata). The
 * webapp's BillingCycle (stores/paymentSelection/types.ts)
 * re-exports this inference.
 */
export const BillingCycle = z.enum(["monthly", "annual"]);
export type BillingCycle = z.infer<typeof BillingCycle>;

/**
 * Mirrors the BillingFeature enum in
 * webapp/lib/billing/constants.ts (which matches the DB
 * usage_feature enum — deprecated features are kept for DB
 * compatibility and still appear in usage responses as no_access
 * rows). The usage handler assigns BillingFeature values directly
 * into this enum's slot, so adding a BillingFeature member without
 * widening this enum is a compile error in
 * app/api/billing/usage/route.ts.
 */
export const BillingFeatureName = z.enum([
  "content_generations",
  "content_strategy",
  "analytics",
  "evaluations",
  "publishing_integrations",
  "collaborative_editing",
  "content_import",
  "gap_content",
  "query_discovery",
  "prompt_optimization",
]);
export type BillingFeatureName = z.infer<typeof BillingFeatureName>;

/**
 * Mirrors FeatureLimitPeriod in
 * webapp/lib/billing/feature-limits.ts.
 */
export const FeatureLimitPeriod = z.enum(["daily", "weekly", "monthly"]);
export type FeatureLimitPeriod = z.infer<typeof FeatureLimitPeriod>;

// ── GET /api/billing/usage ────────────────────────────────────────

/**
 * One per-feature usage entry. All six keys are always present on
 * the wire; `type` discriminates the value correlations the handler
 * guarantees (it builds each branch literally — see
 * app/api/billing/usage/route.ts):
 *
 * - bounded: metered feature — `used` is the period's recorded
 *   usage, `limit`/`remaining` are numbers (remaining is clamped to
 *   >= 0), `period` is the BILLING_PRODUCT_LIMITS window.
 * - unlimited: `used` pinned to 0 (usage isn't counted),
 *   `limit`/`remaining`/`period` all null.
 * - no_access: `used`/`limit`/`remaining` pinned to 0, `period`
 *   null.
 *
 * webapp/hooks/useFeatureUsage.ts re-exports this inference as its
 * FeatureUsage type.
 */
export const FeatureUsage = z.discriminatedUnion("type", [
  z.object({
    feature: BillingFeatureName,
    type: z.literal("bounded"),
    used: z.number(),
    limit: z.number(),
    remaining: z.number(),
    period: FeatureLimitPeriod,
  }),
  z.object({
    feature: BillingFeatureName,
    type: z.literal("unlimited"),
    used: z.number(),
    limit: z.null(),
    remaining: z.null(),
    period: z.null(),
  }),
  z.object({
    feature: BillingFeatureName,
    type: z.literal("no_access"),
    used: z.number(),
    limit: z.number(),
    remaining: z.number(),
    period: z.null(),
  }),
]);
export type FeatureUsage = z.infer<typeof FeatureUsage>;

/**
 * `plan` is the org's effective plan (getOrgEffectivePlan);
 * `features` covers every BILLING_PRODUCT_LIMITS entry for that
 * plan, narrowed by the optional `features` query param.
 */
export const BillingUsageResponse = z.object({
  plan: BillingPlan,
  features: z.array(FeatureUsage),
});
export type BillingUsageResponse = z.infer<typeof BillingUsageResponse>;

// ── POST /api/billing/create-checkout-session ─────────────────────

/**
 * `plan_type`/`billing_cycle` index PLAN_MAP to resolve the Stripe
 * price lookup key (the enums replace the handler's old
 * `!plan_type || !billing_cycle` + PLAN_MAP-miss 400s). `project_id`
 * is appended verbatim to the success URL — `.uuid()` folds in the
 * handler's previous UUID_SCHEMA guard against query-string
 * injection. `context` only changes behavior when it equals
 * "onboarding" (success URL routing); other strings are accepted
 * and ignored. `org_slug` routes the subscription to a team org
 * (requires ownership); when absent the handler falls back to the
 * caller's personal org.
 */
export const CheckoutSessionRequest = z.object({
  plan_type: CheckoutPlanType,
  billing_cycle: BillingCycle,
  context: z.string().optional(),
  project_id: z.string().uuid().optional(),
  org_slug: z.string().optional(),
});
export type CheckoutSessionRequest = z.infer<typeof CheckoutSessionRequest>;

/**
 * Both session routes return only the Stripe-hosted redirect URL
 * (checkout session / billing-portal session). Consumers assign it
 * to window.location.href.
 */
export const StripeUrlResponse = z.object({ url: z.string() });
export type StripeUrlResponse = z.infer<typeof StripeUrlResponse>;

// ── POST /api/billing/create-portal-session ───────────────────────

/**
 * `org_slug` only adds an ownership check (the Stripe customer is
 * per user, so the portal scope doesn't change with org). The
 * handler deliberately tolerates a missing or non-JSON body —
 * webapp/services/billingApi.ts POSTs with no body at all — so the
 * producer parses this leniently (safeParse with a fallthrough to
 * {}), not via parseBody's 400 path.
 */
export const PortalSessionRequest = z.object({
  org_slug: z.string().optional(),
});
export type PortalSessionRequest = z.infer<typeof PortalSessionRequest>;

// ── POST /api/billing/subscription-status ─────────────────────────

/**
 * POST-shaped read: the body only scopes the lookup. `org_id`
 * requires membership (the handler 403s via requireOrgMemberById);
 * omitted, the handler resolves the caller's personal org. Parsed
 * leniently like PortalSessionRequest (safeParse with a fallthrough
 * to {}) — the pre-contract handler treated any unreadable body as
 * {}, falling back to the personal org.
 */
export const SubscriptionStatusRequest = z.object({
  org_id: z.string().optional(),
});
export type SubscriptionStatusRequest = z.infer<
  typeof SubscriptionStatusRequest
>;

/**
 * Single 2xx branch, built in one literal from getOrgSubscription
 * (webapp/lib/billing/feature-access.ts). Deliberately NOT a
 * discriminated union like FeatureUsage: `billing_cycle` /
 * `billing_interval_count` can be null even on a paid plan (legacy
 * rows without a persisted billing_cycle), so the only stable
 * correlation is has_active_subscription === (plan_type !== "free")
 * — documented here rather than encoded, to keep an edge row from
 * turning into a server-side parse failure. `canceled` is always
 * present on the wire (false for the free tier).
 */
export const SubscriptionStatusResponse = z.object({
  has_active_subscription: z.boolean(),
  plan_type: BillingPlan,
  billing_cycle: BillingCycle.nullable(),
  billing_interval_count: z.number().nullable(),
  canceled: z.boolean(),
});
export type SubscriptionStatusResponse = z.infer<
  typeof SubscriptionStatusResponse
>;

// ── POST /api/billing/credits/checkout ────────────────────────────

/**
 * Buy one credit pack for an org (owner only, paid plans only).
 * `price_id` must be an active one-time price on the Stripe credits
 * product; the pack's size comes from that price's
 * `metadata.credits`, never from the client. Returns the Checkout
 * URL as StripeUrlResponse.
 */
export const CreditCheckoutRequest = z.object({
  org_slug: z.string().min(1),
  price_id: z.string().startsWith("price_"),
});
export type CreditCheckoutRequest = z.infer<typeof CreditCheckoutRequest>;
